import PageBanner from "../components/ui/PageBanner";
import surgicalImg from "../assets/images/commitment/commitment-ot.jpeg";
import erImg from "../assets/header-1.jpeg";
import icuImg from "../assets/images/commitment/commitment-ward.jpeg";
import nicuImg from "../assets/images/commitment/commitment-nicu.jpeg";
import dayCareImg from "../assets/images/inpatient/day-care.jpeg";
import nutritionImg from "../assets/images/inpatient/nutrition.jpeg";
import "./InPatientServices.css";

const INPATIENT_SECTIONS = [
  {
    key: "surgical",
    title: "Surgical (OT)",
    label: "In-Patient Services",
    img: surgicalImg,
    alt: "Operating theatre services at Capital Care International Hospital",
    desc:
      "Our surgical services are supported by modern modular operating theatres, trained OT staff, and strict infection-control protocols to support safe procedures and smooth recovery.",
    bullets: [
      "3 modular OTs + 1 minor OT",
      "Pre-op evaluation and post-op monitoring",
      "Sterile protocols and patient safety checks",
      "Planned and emergency surgical support",
    ],
  },
  {
    key: "er",
    title: "Emergency Room (ER) — 24/7",
    label: "In-Patient Services",
    img: erImg,
    alt: "24/7 emergency services at Capital Care International Hospital",
    desc:
      "The Emergency Room is available 24/7 for urgent medical and trauma care with rapid triage, stabilization, and quick access to diagnostics and specialist backup when needed.",
    bullets: [
      "24/7 emergency coverage",
      "Rapid triage and stabilization",
      "Advanced life support readiness",
      "Coordinated admissions for critical cases",
    ],
  },
  {
    key: "icu",
    title: "Intensive Care Unit (ICU)",
    label: "In-Patient Services",
    img: icuImg,
    alt: "Intensive Care Unit at Capital Care International Hospital",
    desc:
      "Our ICU provides close monitoring and advanced support for critically ill patients. Care is delivered by experienced teams focused on timely interventions and continuous observation.",
    bullets: [
      "6-bed ICU capacity",
      "Continuous monitoring and critical care support",
      "Post-operative and high-dependency care",
      "Coordinated specialist input as required",
    ],
  },
  {
    key: "nicu",
    title: "Neonatal Intensive Care Unit (NICU)",
    label: "In-Patient Services",
    img: nicuImg,
    alt: "Neonatal Intensive Care Unit at Capital Care International Hospital",
    desc:
      "Our NICU is dedicated to newborn care with specialized monitoring and support. We focus on gentle, family-centered neonatal care with safety and comfort as priorities.",
    bullets: [
      "10-bed NICU capacity",
      "Specialized neonatal monitoring and support",
      "Coordination with pediatrics and obstetrics teams",
      "Family guidance and discharge planning",
    ],
  },
  {
    key: "daycare-minor-procedures",
    title: "Day Care Procedure",
    label: "In-Patient Services",
    img: dayCareImg,
    alt: "Day care and minor procedures services at Capital Care International Hospital",
    desc:
      "Our day care in-patient services support planned short-stay admissions for procedures, monitoring, and treatments where overnight stay may not be required. We also provide minor procedures and wound dressing services with appropriate sterile technique, patient comfort measures, and clear after-care instructions.",
    bullets: [
      "Planned short-stay admissions",
      "Observation and monitoring as advised",
      "Same-day procedures and treatments",
      "Discharge planning and follow-up guidance",
      "Minor procedures support",
      "Wound care and dressing changes",
      "Infection prevention and sterile technique",
      "After-care instructions and follow-up scheduling",
    ],
  },
  {
    key: "nutrition-cafe",
    title: "In House Nutritional Cafe",
    label: "In-Patient Services",
    img: nutritionImg,
    alt: "In house nutritional cafe services at Capital Care International Hospital",
    desc:
      "We support in-patients and attendants with nutrition guidance and diet planning aligned with clinical needs to help improve recovery and overall wellbeing during admission.",
    subsections: [
      {
        title: "Patient Diet Plan",
        desc: "Personalized diet plans coordinated with your treating consultant to support recovery and ongoing clinical needs (e.g., diabetic, cardiac, renal, or post-surgery diets).",
      },
    ],
  },
];

function ServiceSection({ section, reverse = false }) {
  return (
    <div className={`inpatient__row ${reverse ? "inpatient__row--reverse" : ""}`}>
      <div className="inpatient__media">
        <img className="inpatient__img" src={section.img} alt={section.alt} loading="lazy" />
      </div>
      <div className="inpatient__content">
        <p className="section-label">{section.label}</p>
        <h3 className="section-title inpatient__title">{section.title}</h3>
        <p className="section-text inpatient__text">{section.desc}</p>
        {section.subsections?.length > 0 && (
          <div className="inpatient__subsections">
            {section.subsections.map((sub) => (
              <div key={sub.title} className="inpatient__subsection">
                <h3 className="section-title inpatient__subtitle">{sub.title}</h3>
                <p className="section-text inpatient__text">{sub.desc}</p>
              </div>
            ))}
          </div>
        )}
        {section.bullets?.length > 0 && (
          <ul className="inpatient__list">
            {section.bullets.map((b) => (
              <li key={b}>{b}</li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

export default function InPatientServices() {
  return (
    <>
      <PageBanner title="In-Patient Services" crumbs={["Home", "Our Services", "In-Patient"]} />

      <section className="section">
        <div className="section-inner">
          <div className="inpatient__intro">
            <h3 className="section-title">In-Patient Care at CCIH</h3>
            <p className="section-text">
              In-patient care includes surgical services, 24/7 emergency support, and specialized critical care units.
              Explore the key areas below.
            </p>
          </div>

          <div className="inpatient__stack">
            {INPATIENT_SECTIONS.map((section, i) => (
              <ServiceSection key={section.key} section={section} reverse={i % 2 === 1} />
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
